/**
 * The fields an insurance breakdown row needs for display derivation.
 *
 * Declared structurally so the quote, rental and public insurance rows all
 * satisfy it.
 */
export interface InsuranceDisplayRow {
  unit?: string | null;
  unit_price: string;
  total: string;
}

/**
 * The amount an insurance row should display next to its unit label.
 *
 * Persisted insurance line items store the option TOTAL in `unit_price` (with
 * quantity 1), so a breakdown rebuilt from a saved order arrives with
 * `unit_price == fee * days` and a naive render shows "$47.97/day" for a
 * $15.99/day option over 3 days. For a per-day row the shown amount is
 * therefore derived as `total / days`, which is correct for both data shapes:
 * a freshly calculated breakdown (`unit_price` = fee, `total` = fee * days)
 * and a rebuilt one (`unit_price` = total).
 *
 * Display only — the row's `total` is never recomputed or altered.
 *
 * @param option - Insurance breakdown row
 * @param days - Rental length in days (`breakdown.base_rental.days`)
 * @returns Decimal string with the amount to render before the unit label
 */
export function insurancePerDayAmount(
  option: InsuranceDisplayRow,
  days: number | null | undefined,
): string {
  if (option.unit !== "per_day") {
    return option.unit_price;
  }

  const total = Number.parseFloat(option.total);

  if (!Number.isFinite(total) || !days || days <= 0) {
    return option.unit_price;
  }

  return (Math.round((total / days) * 100) / 100).toFixed(2);
}
